import { useMemo } from "react";
import { DataGridColumnConfig, DataGridFilterType } from "./types";
import { isEmpty, isEqual, isNil, startCase } from "lodash";
import { Tag } from "components/Tag";
import classNames from "classnames";
import useMediaQuery from "components/utils/useMediaQuery";
import { Button } from "components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getColumnFilterInputType } from "./utils";

type HeaderData = {
    columnLabel: string;
    filterValue: string | undefined;
    isFiltered: boolean;
    column: DataGridColumnConfig;
};
export const DataGridFilterRowHeader: React.FC<{
    className?: string;
    columns: DataGridColumnConfig[];
    filter?: DataGridFilterType;
    filterSelected?: string;
    sortBy?: string;
    sortAsc?: boolean;
    isFilterEmpty: boolean;
    showSortBy?: boolean;
    allowActiveAll?: boolean;
    toggleFilter: (forceOpen?: boolean) => void;
    toggleSort: (status?: boolean) => void;
    toggleFilterSetting?: () => void;
    onFilterInputFocus: (key: string) => void;
    onFieldClean?: (key: string) => void;
    onFilterClear?: () => void;
}> = ({
    className,
    columns,
    filter = {},
    filterSelected,
    sortBy,
    sortAsc,
    isFilterEmpty,
    showSortBy = true,
    allowActiveAll = true,
    toggleFilter,
    toggleSort,
    toggleFilterSetting,
    onFilterInputFocus,
    onFieldClean,
    onFilterClear,
}) => {
    const isMobile = useMediaQuery("(max-width: 768px)");

    const headers = useMemo<Record<string, HeaderData>>(() => {
        return columns.reduce((acc, column) => {
            const columnLabel = startCase(column.name);
            const filterValue = filter[column.key];

            acc[column.key] = {
                columnLabel,
                filterValue,
                isFiltered: !isEmpty(filterValue),
                column,
            };

            return acc;
        }, {} as Record<string, HeaderData>);
    }, [columns, filter]);

    const filterCount = Object.values(headers).filter((header) => header.isFiltered).length;

    const onClick = (key: string) => {
        toggleFilter?.(true);
        onFilterInputFocus(key);
    };

    return (
        <div
            style={isMobile ? { scrollbarWidth: "none" } : {}}
            className={classNames(className, "d-flex gap-2", { "overflow-x-auto overflow-y-hidden w-100": isMobile })}
        >
            {isMobile && toggleFilterSetting && (
                <Button
                    key="sliders"
                    style={{
                        color: "initial",
                        borderColor: "var(--bg-active) !important",
                    }}
                    className="flex-shrink-0 bg-transparent d-flex align-items-center px-2 py-0"
                    onClick={toggleFilterSetting}
                >
                    <FontAwesomeIcon className="visible pe-2" icon={"sliders"} size="sm" />
                    <span>{filterCount}</span>
                </Button>
            )}
            {isMobile && showSortBy && !isNil(sortBy) && !isNil(sortAsc) && (
                <Tag
                    className={classNames("flex-shrink-0 btn-filter-header", {
                        activated: sortBy.length,
                    })}
                    value="Sort by"
                    preIcon={"arrow-up-arrow-down"}
                    onAdd={() => toggleSort()}
                />
            )}
            {Object.entries(headers).map(([key, { columnLabel, filterValue, isFiltered, column }]) => (
                <Tag
                    key={key}
                    className={classNames(
                        "text-nowrap btn-filter-header",
                        {
                            activated: isFiltered && !isMobile,
                        },
                        {
                            activated: allowActiveAll ? isMobile && isFiltered : isEqual(filterSelected, key),
                        }
                    )}
                    valueClassName={classNames("text-truncate", {
                        "text-uppercase": getColumnFilterInputType(column) === "select" && isFiltered,
                    })}
                    valueStyle={{ maxWidth: "20ch" }}
                    title={isFiltered ? columnLabel : ""}
                    value={isFiltered ? filterValue : columnLabel}
                    showPreIcon={allowActiveAll ? !isFiltered : true}
                    onAdd={() => onClick(key)}
                    onRemove={isFiltered && onFieldClean ? () => onFieldClean(key) : undefined}
                />
            ))}
            {!isFilterEmpty && onFilterClear && (
                <Button key="clear" variant="link" className="py-0 border-0 text-decoration-none text-nowrap" onClick={onFilterClear}>
                    Clear all
                </Button>
            )}
        </div>
    );
};
